import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Skeleton,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import PurchasePaymentAdd from "./PurchasePaymentAdd";
import PurchasePaymentEdit from "./PurchasePaymentEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { printPurchasePaymentThermal } from "../utils/printPurchasePaymentThermal";
import PrintIcon from "@mui/icons-material/Print";

const useDebouncedValue = (value, delay = 350) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
};

const PurchasePaymentIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  const queryParams = new URLSearchParams({
    page: String(page),
    page_size: String(rowsPerPage),
  });
  if (debouncedSearchTerm.trim()) {
    queryParams.set("search", debouncedSearchTerm.trim());
  }

  const { data, isLoading, isError } = useFetchObjects(
    ["purchase_payment", debouncedSearchTerm, page, rowsPerPage],
    `shop/purchase-payments/?${queryParams.toString()}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete(
    "shop/purchase-payments",
    token
  );

  const payments = data && data.results ? data.results : [];
  const totalCount = data && data.count ? data.count : payments.length;

  if (isError) {
    return <ErrorPage message={t("purchasePayment.failedToLoadPayments")} />;
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("purchasePayment.paymentList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("purchasePayment.addPayment")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("stock.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>

      {/* Search Field */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("purchasePayment.searchPayments")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Purchase Payment Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 750 }} aria-label="payment table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.id")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.supplier")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.purchaseId")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.paymentDate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchasePayment.notes")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("purchase.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Supplier",
                      "Purchase ID",
                      "Amount",
                      "Currency",
                      "Payment Date",
                      "Notes",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : payments.map((payment, index) => (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{payment.purchase_info?.supplier || 'N/A'}</TableCell>
                    <TableCell>
                      <strong>Purchase #{payment.purchase_info?.id || payment.purchase || 'N/A'}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{payment.amount}</strong>
                    </TableCell>
                    <TableCell>
                      {payment.currency_details?.title || "-"}
                    </TableCell>
                    <TableCell>
                      {new Date(payment.payment_date).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {payment.notes || "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="success"
                        size="small"
                        sx={{ mr: 1, minWidth: 'auto', p: 1 }}
                        onClick={() => {
                          // Calculate remaining amount from purchase details
                          const purchaseDetails = payment?.purchase_info;
                          const totalRemained = purchaseDetails?.total_remained_amount || {};
                          const currencyTitle = payment?.currency_details?.title || 'AF';
                          const remainingAmount = totalRemained[currencyTitle] || 0;
                          
                          printPurchasePaymentThermal({
                            receiptNumber: payment?.id || Math.floor(Math.random() * 9999),
                            date: new Date(payment.payment_date).toLocaleDateString(),
                            supplierName: payment?.purchase_info?.supplier || '',
                            purchaseId: payment?.purchase_info?.id || payment?.purchase || 'N/A',
                            requestedAmount: `${payment.amount}`,
                            remainingAmount: `${remainingAmount}`,
                            currencySymbol: payment?.currency_details?.title || 'AF',
                            paymentMethod: payment?.payment_method || 'Cash',
                            notes: payment?.notes || ''
                          });
                        }}
                      >
                        <PrintIcon />
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(payment);
                          setOpenEdit(true);
                        }}
                      >
                        {t("stock.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(payment.id)}
                      >
                        {t("stock.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography>
            {t("purchasePayment.rowsPerPage", "Rows Per Page")}
          </Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
        />
      </Grid>

      {openAdd && (
        <PurchasePaymentAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openEdit && (
        <PurchasePaymentEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}

      <ConfirmDialog />
    </Box>
  );
};

export default PurchasePaymentIndex;
